import { Button } from 'components/atoms/Button';
import { AIToolConsentModal } from 'components/molecules/AIToolConsentModal';
import { useAIChatContext } from 'components/organisms/AIChat/AIChat.context';
import useAIUsageConsent from 'hooks/shared/useAIUsageConsent';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ConversationMessageConsentPart,
  ConversationMessagePart,
} from 'types/ai.types';
import type { AIV3UsageConsent } from 'types/users.types';
import {
  AIFeatureUsageConsentEnum,
  AIToolUsageConsentEnum,
} from 'types/users.types';
import { logAmplitude } from 'utils/amplitude';
import {
  WEB_AIASSISTANT_ALLOWALLCONSENTS,
  WEB_AIASSISTANT_DENYALLCONSENTS,
} from 'utils/amplitude/events/ai.amplitude';
import usePatchConversationMessage from './usePatchConversationMessage';

type AIMessagePartConsentProps = {
  messageId: string;
  messagePart: ConversationMessageConsentPart;
  messageParts: ConversationMessagePart[];
  isLastPart?: boolean;
};

const AIMessagePartConsent: FC<AIMessagePartConsentProps> = ({
  messageId,
  messagePart,
  messageParts,
  isLastPart,
}) => {
  const { t } = useTranslation();
  const { handleRegenerateMessage, conversation, isStreaming } =
    useAIChatContext();
  const { patchConsents, isLoading, consents } = useAIUsageConsent();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { patchMessage } = usePatchConversationMessage(
    conversation?._id || '',
    messageId,
  );

  const patchAllAndRegenerate = useCallback(
    async (consent: AIToolUsageConsentEnum) => {
      try {
        setIsSubmitted(true);

        const newConsents: AIV3UsageConsent = {
          expenses: consent,
          revenues: consent,
          taxes: consent,
          summaries: consent,
          vehicles: consent,
          documents_repository: consent,
          ai_tax_advisor: AIFeatureUsageConsentEnum.allow, // allow AI tax advisor as per requirements
        };

        await patchConsents(newConsents);
        await patchMessage({
          allowed_data_access_on_form: true,
          parts: messageParts,
        });
        handleRegenerateMessage(messageId);
      } catch (error) {
        setIsSubmitted(false);
      }
    },
    [
      patchConsents,
      patchMessage,
      handleRegenerateMessage,
      messageId,
      messageParts,
    ],
  );

  // share all data
  const handleShareAll = useCallback(async () => {
    setIsSubmitted(true);
    logAmplitude(WEB_AIASSISTANT_ALLOWALLCONSENTS);
    await patchAllAndRegenerate(AIToolUsageConsentEnum.allow);
  }, [patchAllAndRegenerate]);

  // deny all data
  const handleDeny = useCallback(async () => {
    logAmplitude(WEB_AIASSISTANT_DENYALLCONSENTS);
    await patchAllAndRegenerate(AIToolUsageConsentEnum.deny);
  }, [patchAllAndRegenerate]);

  // customise consents using modal
  const handleOpenModal = useCallback(() => setIsModalOpen(true), []);
  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  const handleModalSave = useCallback(
    async (consents: AIV3UsageConsent) => {
      setIsModalOpen(false);
      setIsSubmitted(true);
      await patchConsents(consents);

      await patchMessage({
        allowed_data_access_on_form: true,
        parts: messageParts,
      });
      handleRegenerateMessage(messageId);
    },
    [
      messageId,
      handleRegenerateMessage,
      patchConsents,
      patchMessage,
      messageParts,
    ],
  );

  const handleRegenerate = useCallback(() => {
    setIsSubmitted(true);
    handleRegenerateMessage(messageId);
  }, [handleRegenerateMessage, messageId]);

  if (consents.ai_tax_advisor === AIFeatureUsageConsentEnum.allow) {
    return (
      <div className="flex flex-col max-w-[80%] space-y-4">
        <p className="text-sm text-gray-600">{messagePart.content}</p>

        {isLastPart && !isStreaming && (
          <AnimatePresence initial={false}>
            {!isSubmitted && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.2 }}
                className="space-x-3 flex flex-row"
              >
                <Button
                  size="sm"
                  color="taxes"
                  onClick={handleRegenerate}
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {t('ai_bot.initial_consent.regenerate.title')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col max-w-[80%] space-y-4">
        <p className="text-sm text-gray-600">{messagePart.content}</p>

        <AnimatePresence initial={false}>
          {!isSubmitted && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
              className="space-x-3 flex flex-row"
            >
              <Button
                size="sm"
                color="taxes"
                onClick={handleShareAll}
                loading={isLoading}
                disabled={isLoading}
              >
                {t('ai_bot.initial_consent.share_all.title')}
              </Button>

              <Button
                size="sm"
                structure="secondary"
                color="primary"
                className="!border-primary-100"
                onClick={handleOpenModal}
                loading={isLoading}
                disabled={isLoading}
              >
                {t('ai_bot.initial_consent.choose.title')}
              </Button>

              <Button
                size="sm"
                structure="secondary"
                color="primary"
                className="!border-primary-100"
                onClick={handleDeny}
                loading={isLoading}
                disabled={isLoading}
              >
                {t('ai_bot.initial_consent.deny.title')}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AIToolConsentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleModalSave}
      />
    </>
  );
};

export default AIMessagePartConsent;
